import React from 'react';
import classNames from 'classnames';
import {
  View,
  Text,
} from 'react-native';

import { Inline, Box } from '@styleseat/ui';
import { colors } from '@styleseat/brand';
import GlyphIcon, { CHEVRON_UP, CHEVRON_DOWN } from '../../../ui/GlyphIcon';
import {
  HeaderText,
  BodyText,
} from '../../../ui';
import formatPriceAndDuration from '../../../../modules/formatters/formatPriceAndDuration';
import {
  SubsetOfFieldsFromFormattedService,
} from '../../../../modules/Api.types';
import './ServiceInfo.module.scss';
import { useCanRender, useInTest } from '../../../../hooks';
import { CLIENT_PROPROFILE_POPULAR_TAG_TEST_NAME } from '../constants';

const ServiceInfo = ({
  formattedDiscount,
  service,
  showAndUp,
  isExpanded,
  description,
  useShortDescription,
  showFullDescription,
  showExpandButton,
  onInfoClick,
  renderSeeReviewsLink,
  hideTags,
}: {
  formattedDiscount: string,
  service: SubsetOfFieldsFromFormattedService<'id' | 'name' | 'cost' | 'total_duration_minutes' | 'tags'> | null,
  showAndUp: boolean,
  isExpanded: boolean,
  description: string,
  useShortDescription: boolean,
  showFullDescription: boolean,
  showExpandButton: boolean,
  onInfoClick: () => void,
  renderSeeReviewsLink: () => any,
  hideTags?: boolean,
}) => {
  const MAX_LENGTH = 80;
  const hasDescription = !!description;
  const truncate = (text: string) => (text.length > MAX_LENGTH ? `${text.substring(0, MAX_LENGTH)}...` : text);
  const seeReviewsLink = renderSeeReviewsLink();
  const inPopularTagTest = useInTest(CLIENT_PROPROFILE_POPULAR_TAG_TEST_NAME);
  const canRenderTags = useCanRender(
    inPopularTagTest,
    service?.tags,
    service?.tags?.length,
    !hideTags,
  );

  return (
    <React.Fragment>
      <View
        // @ts-ignore
        accessibilityRole="banner"
        styleName="header"
      >
        {!!formattedDiscount && (
          <Text styleName="promoAmount">{`${formattedDiscount} off`}</Text>
        )}
        <HeaderText
          level={4}
          styleName={classNames({ useShortDescription })}
          testID="serviceTitle"
        >
          {service?.name}
        </HeaderText>
        {canRenderTags && (
          <Inline>
            {service.tags.map(tag => (
              <Box
                key={service.id}
                borderRadius={12}
                paddingY={2}
                paddingX={8}
                justifyContent="center"
                alignItems="center"
                color={colors.green}
                testID={`service-info-tags-${service.id}`}
              >
                <BodyText
                  appearance="medium"
                  color={colors.whiteWash}
                  testID={`service-info-tags-${service.id}-${tag}`}
                >
                  {tag}
                </BodyText>
              </Box>
            ))}
          </Inline>
        )}
      </View>
      <Inline alignItems="center" testID="summary">
        <BodyText appearance="medium" testID="serviceDetails">
          {service ? formatPriceAndDuration(service, showAndUp) : ''}
        </BodyText>
      </Inline>

      {(hasDescription || seeReviewsLink) && (
        <View styleName="descriptionContainer" testID="descriptionContainer">
          {hasDescription ? (
            <View styleName="itemDescription" testID="itemDescription">
              <BodyText
                appearance="medium"
                weight="thin"
                styleName={classNames({ useShortDescription })}
                testID="description_section"
                whiteSpace="normal"
              >
                {isExpanded || showFullDescription ? description : truncate(description)}
                {' '}
                {
                  showExpandButton ? (
                    <GlyphIcon
                      onPress={onInfoClick}
                      content={isExpanded ? CHEVRON_UP : CHEVRON_DOWN}
                    />
                  ) : null
                }
              </BodyText>
              {seeReviewsLink}
            </View>
          ) : (
            <View styleName="itemDescription">
              {seeReviewsLink}
            </View>
          )}
        </View>
      )}
    </React.Fragment>
  );
};

export default ServiceInfo;
